import { useState, useRef } from 'react'
import { useSession } from '@/context/AuthContext'
import { X, ArrowRight, User, ChevronDown, Check } from 'lucide-react'
import { picColor } from '@/lib/utils'
import { TaskRecommendationModal } from '@/components/TaskRecommendationModal'
import { useClickOutside } from '@/lib/useClickOutside'
import { useRegisteredUsers, type RegisteredUserLite } from '@/lib/useRegisteredUsers'
import { VisibilityPicker } from '@/components/VisibilityPicker'
import { visibilityNeedsViewers, type Visibility } from '@/lib/roles'
import type { Project } from '@/lib/types'

interface Props {
  onClose: () => void
  initialName?: string
  initialDescription?: string
  onCreated?: (project: Project) => void
}

export type ProjectDraft = {
  name: string
  description: string
  color: string
  pic: string | null
  picEmail: string | null
  visibility: Visibility
  /** Mode 'custom': orang yang boleh melihat project ini (jadi anggota project). */
  viewerEmails: string[]
}

const inputClass =
  'w-full px-3.5 py-2.5 bg-[#F8F7F3] border border-[#E0DFD8] rounded-xl text-[#111110] text-sm font-body placeholder:text-[#C8C7BF] focus:outline-none focus:border-[#111110] focus:bg-white transition-all'

// Dropdown pemilih PIC dari daftar user terdaftar.
function PicDropdown({
  value,
  onChange,
  users,
}: {
  value: string
  onChange: (v: string) => void
  users: RegisteredUserLite[]
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, () => setOpen(false), open)

  // Pastikan nilai terpilih saat ini selalu muncul sebagai opsi
  const names = Array.from(
    new Set([value, ...users.map((u) => u.username)].filter(Boolean)),
  )

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 bg-[#F8F7F3] border border-[#E0DFD8] rounded-xl hover:border-[#C8C7BF] focus:outline-none focus:border-[#111110] transition-all"
      >
        <span className="flex items-center gap-2 min-w-0">
          {value ? (
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
              style={{ backgroundColor: picColor(value) }}
            >
              {value.charAt(0).toUpperCase()}
            </span>
          ) : (
            <User className="w-3.5 h-3.5 text-[#AEADA8] flex-shrink-0" />
          )}
          <span className="text-sm font-body text-[#111110] truncate">
            {value || 'Pilih PIC'}
          </span>
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-[#AEADA8] flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 max-h-56 overflow-auto bg-white border border-[#E0DFD8] rounded-xl shadow-xl py-1.5 animate-fade-in"
        >
          {names.length === 0 && (
            <p className="px-3.5 py-2 text-xs text-[#AEADA8] font-body">
              Belum ada user terdaftar
            </p>
          )}
          {names.map((name) => (
            <button
              key={name}
              type="button"
              role="option"
              aria-selected={name === value}
              onClick={() => {
                onChange(name)
                setOpen(false)
              }}
              className={`w-full flex items-center gap-2 px-3.5 py-2 text-sm font-body transition-colors ${
                name === value
                  ? 'bg-[#F4F3F0] text-[#111110] font-semibold'
                  : 'text-[#6B6860] hover:bg-[#F4F3F0]'
              }`}
            >
              <span
                className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                style={{ backgroundColor: picColor(name) }}
              >
                {name.charAt(0).toUpperCase()}
              </span>
              <span className="truncate flex-1 text-left">{name}</span>
              {name === value && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function CreateProjectModal({ onClose, initialName, initialDescription, onCreated }: Props) {
  // Draft project — project baru dibuat BERSAMA task di step berikutnya.
  const [draft, setDraft] = useState<ProjectDraft | null>(null)

  // ── Isian project (manual sepenuhnya) ──────────────────────────
  const [fName, setFName] = useState(initialName ?? '')
  const [fDesc, setFDesc] = useState(initialDescription ?? '')

  const { data: session } = useSession()
  const { users: registeredUsers } = useRegisteredUsers()
  // Default PIC = username akun login DI SISTEM, dicocokkan lewat EMAIL.
  // (Nama Google bisa beda dengan username di sistem — mis. akun bernama Google
  // "Dimas Pujangga" tapi username sistemnya "Dims".) Fallback ke nama/email session.
  const myEmail = (session?.user?.email ?? '').toLowerCase()
  const me =
    registeredUsers.find((u) => u.email.toLowerCase() === myEmail)?.username
    || session?.user?.name
    || session?.user?.email
    || ''
  const [picOverride, setPicOverride] = useState<string | null>(null)
  const pic = picOverride ?? me

  // Siapa yang boleh melihat project ini. Default terbuka se-BU; picker hanya
  // muncul untuk non-Member (lihat VisibilityPicker).
  const [visibility, setVisibility] = useState<Visibility>('public')
  const [viewerEmails, setViewerEmails] = useState<string[]>([])

  const handleSave = () => {
    const name = fName
    const desc = fDesc
    if (!name.trim()) return
    // Belum simpan ke DB — lanjut ke step rekomendasi, project+task disimpan bareng di sana
    const meEmail = session?.user?.email ?? ''
    const picUser = registeredUsers.find((u) => u.username === pic)
    const picEmail = picUser?.email ?? (pic && pic === me ? meEmail : null)
    // Warna ditentukan server dari departemen pembuat — kirim string kosong.
    setDraft({
      name: name.trim(), description: desc.trim(), color: '', pic: pic || null, picEmail,
      visibility, viewerEmails,
    })
  }

  const canSave = !!fName.trim()
    && (!visibilityNeedsViewers(visibility) || viewerEmails.length > 0)

  // ── Step rekomendasi task: project + task dibuat BERSAMAAN di sini ──
  if (draft) {
    return <TaskRecommendationModal draft={draft} onClose={onClose} onCreated={onCreated} />
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-fade-in" onClick={onClose} />

      {/* max-h + flex-col: isi form bisa lebih tinggi dari viewport (hasil AI + picker
          visibility), jadi body-nya yang scroll — bukan modal terpotong. */}
      <div className="relative w-full max-w-[520px] max-h-[90vh] flex flex-col bg-white border border-[#E0DFD8] rounded-2xl shadow-2xl shadow-black/15 animate-slide-up">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F2F1EC] flex-shrink-0">
          <h2 className="font-display text-[15px] font-bold text-[#111110]">Project Baru</h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-[#F2F1EC] text-[#AEADA8] hover:text-[#111110] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-4">

          <div>
            <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">
              Nama Project <span className="text-[#DC2626] font-normal text-xs">*wajib</span>
            </label>
            <input type="text" value={fName} onChange={e => setFName(e.target.value)}
              placeholder="cth: Kampanye Marketing Q3" className={inputClass} autoFocus />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">
              Deskripsi <span className="text-[#AEADA8] font-normal text-xs">opsional</span>
            </label>
            <textarea value={fDesc} onChange={e => setFDesc(e.target.value)}
              placeholder="Tujuan dan scope project ini..." rows={3}
              className={`${inputClass} resize-none leading-relaxed`} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">PIC</label>
            <PicDropdown value={pic} onChange={setPicOverride} users={registeredUsers} />
          </div>

          {/* Siapa yang bisa melihat project ini (hanya untuk non-Member) */}
          <VisibilityPicker value={visibility} onChange={setVisibility}
            viewerEmails={viewerEmails} onViewerEmailsChange={setViewerEmails} />

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-body text-[#8A8A85] hover:text-[#111110] transition-colors">
              Batal
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              className="flex items-center gap-2 px-5 py-2 bg-[#111110] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2A2A28] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              Lanjut <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
